import React, { useCallback, useId, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Modal } from 'reactstrap';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation } from '@fortawesome/pro-light-svg-icons/faCircleExclamation';
import { TypedMemo } from '../../../types';
import { ConfirmDialogProps } from './ConfirmDialogProps';
import { useSwipeDownToClose } from './useSwipeDownToClose';
import { withBrandWord } from '../../../../music24/components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the dialog and its marks; each part uses its own slice of it */
import styles from './ConfirmDialog.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The confirm dialog of the web app (design: dialogs a1-a10).
 *
 * The title is in sentence case and names the thing. A destructive action is red; the brand
 * gradient stays for an action that makes or keeps something. The buttons sit on the right,
 * Cancel first. Below 576px the dialog is a bottom sheet with full-width buttons, the action on
 * top. While `isBusy` is true, nothing closes the dialog and both buttons are off.
 */
const ConfirmDialogComponent: React.FC<ConfirmDialogProps> = ({
  isOpen,
  tone,
  title,
  description,
  children,
  mark,
  confirmLabel,
  cancelLabel,
  isBusy = false,
  isConfirmDisabled = false,
  error,
  footerNote,
  onConfirm,
  onCancel,
  isTitleMasked = false,
  testId,
}) => {
  const intl = useIntl();
  const titleId = useId();
  const descriptionId = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const close = isBusy ? undefined : onCancel;
  const swipe = useSwipeDownToClose(close);
  const hasDescription = description !== undefined && description !== null && description !== '';

  // A destructive confirm puts the focus on Cancel, so a stray Enter keeps the data (a6).
  const focusFirstButton = useCallback(() => {
    const target = tone === 'danger' || isConfirmDisabled ? cancelRef.current : confirmRef.current;
    target?.focus();
  }, [isConfirmDisabled, tone]);

  // reactstrap puts `role` and `aria-labelledby` on the `.modal` element but has no prop for the
  // description, so the ref adds it there.
  const setModalElement = useCallback(
    (element: HTMLElement | null) => {
      if (element === null) {
        return;
      }
      if (hasDescription) {
        element.setAttribute('aria-describedby', descriptionId);
      } else {
        element.removeAttribute('aria-describedby');
      }
    },
    [descriptionId, hasDescription]
  );

  return (
    <Modal
      isOpen={isOpen}
      toggle={close}
      centered
      backdrop={isBusy ? 'static' : true}
      keyboard={!isBusy}
      role={tone === 'danger' ? 'alertdialog' : 'dialog'}
      labelledBy={titleId}
      trapFocus
      returnFocusAfterClose
      onOpened={focusFirstButton}
      innerRef={setModalElement}
      className={styles.dialog}
      contentClassName={styles.content}
      data-testid={testId}
    >
      <div className={styles.sheet} onTouchStart={swipe.onTouchStart} onTouchEnd={swipe.onTouchEnd}>
        <span className={styles.grab} aria-hidden="true" />
        <div className={styles.head}>
          {mark !== undefined && <div className={styles.mark}>{mark}</div>}
          <div className={styles.text}>
            <h2 id={titleId} className={styles.title} data-clarity-mask={isTitleMasked ? 'true' : undefined}>
              {withBrandWord(title)}
            </h2>
            {hasDescription && (
              <div id={descriptionId} className={styles.description}>
                {withBrandWord(description)}
              </div>
            )}
          </div>
        </div>
        {children !== undefined && children !== null && <div className={styles.extra}>{children}</div>}
        {error !== undefined && error !== '' && (
          <div className={styles.error} role="alert">
            <FontAwesomeIcon icon={faCircleExclamation} className={styles.errorIcon} />
            <span>{withBrandWord(error)}</span>
          </div>
        )}
        <div className={styles.footer}>
          {footerNote !== undefined && <div className={styles.footerNote}>{footerNote}</div>}
          <button ref={cancelRef} type="button" className={styles.cancel} onClick={onCancel} disabled={isBusy}>
            {cancelLabel ?? intl.formatMessage({ id: 'app.forms.buttons.cancel.label', defaultMessage: 'Cancel' })}
          </button>
          <button
            ref={confirmRef}
            type="button"
            className={clsx(styles.confirm, tone === 'danger' ? styles.danger : styles.primary)}
            onClick={onConfirm}
            disabled={isBusy || isConfirmDisabled}
            aria-busy={isBusy}
          >
            {isBusy && <span className={styles.spinner} aria-hidden="true" />}
            {withBrandWord(confirmLabel)}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export const ConfirmDialog = TypedMemo(ConfirmDialogComponent);
